import axios, { AxiosError, AxiosResponse } from "axios";
import { getCookie } from "./cookies";
import type { ApiError } from "@services/utils/apiHelpers";

const isLocalhost = window.location.hostname === "localhost";
const baseURL = isLocalhost
  ? "https://localhost:8000"
  : `https://${window.location.hostname}`; // Force HTTPS in production
export const getServiceUrl = () => {
  return baseURL;
};

// Create an Axios instance with the dynamic baseURL
const axiosInstance = axios.create({
  baseURL: baseURL,
  withCredentials: true,
});

// Helper to check if response is an API error
function isApiError(data: any, status?: number): data is ApiError {
  return (
    status !== undefined && status !== 200
    // (data &&
    //   typeof data === "object" &&
    //   "error" in data &&
    //   typeof data.error === "string" &&
    //   data.error !== "")
  );
}

// Typed API client with automatic error handling
export const apiClient = {
  ...axiosInstance,

  async get<TResponse>(url: string, config?: any): Promise<TResponse> {
    const response = await axiosInstance.get<TResponse | ApiError>(url, config);
    if (isApiError(response.data, response.status)) {
      throw new Error(response.data?.error || `HTTP ${response.status} Error`);
    }
    return response.data;
  },

  async postWithFullResponse<TResponse, TRequest = any>(
    url: string,
    data?: TRequest,
    config?: any
  ): Promise<AxiosResponse<TResponse, any>> {
    let response;
    try {
      response = await axiosInstance.post<TResponse | ApiError>(
        url,
        data,
        config
      );
    } catch (e: any) {
      throw new Error(
        e.response?.data?.error || `HTTP ${e.response?.status} Error`
      );
    }

    if (isApiError(response.data, response.status)) {
      throw new Error(response.data?.error || `HTTP ${response.status} Error`);
    }
    return response as AxiosResponse<TResponse, any>;
  },

  async post<TResponse, TRequest = any>(
    url: string,
    data?: TRequest,
    config?: any
  ): Promise<TResponse> {
    let response = await this.postWithFullResponse<TResponse, TRequest>(
      url,
      data,
      config
    );
    return response.data;
  },

  async put<TResponse, TRequest = any>(
    url: string,
    data?: TRequest,
    config?: any
  ): Promise<TResponse> {
    const response = await axiosInstance.put<TResponse | ApiError>(
      url,
      data,
      config
    );
    if (isApiError(response.data, response.status)) {
      throw new Error(response.data?.error || `HTTP ${response?.status} Error`);
    }
    return response.data;
  },

  async patch<TResponse, TRequest = any>(
    url: string,
    data?: TRequest,
    config?: any
  ): Promise<TResponse> {
    const response = await axiosInstance.patch<TResponse | ApiError>(
      url,
      data,
      config
    );
    if (isApiError(response.data, response.status)) {
      throw new Error(response.data?.error || `HTTP ${response.status} Error`);
    }
    return response.data;
  },

  async delete<TResponse>(url: string, config?: any): Promise<TResponse> {
    const response = await axiosInstance.delete<TResponse | ApiError>(
      url,
      config
    );
    if (isApiError(response.data, response.status)) {
      throw new Error(response.data?.error || `HTTP ${response.status} Error`);
    }
    return response.data;
  },

  // Native fetch method that maintains authentication and error handling
  async fetch<TResponse>(
    url: string,
    options: RequestInit = {}
  ): Promise<TResponse> {
    const fullUrl = url.startsWith("http") ? url : `${baseURL}${url}`;

    const response = await fetch(fullUrl, {
      ...options,
      credentials: "include", // Use cookies for authentication
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    if (isApiError(data, response.status)) {
      throw new Error(data?.error || `HTTP ${response.status} Error`);
    }

    return data;
  },
};

// Helper to extract ApiError from axios errors
export function getApiError(error: any): ApiError | null {
  if (
    error instanceof AxiosError &&
    error.response?.data &&
    isApiError(error.response.data, error.response.status)
  ) {
    return error.response.data;
  }
  return null;
}
